import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  getParameterMetadata, 
  getParameterValueMetadata,
  isAiParameter, 
  isPremiumParameter, 
  isAdvancedParameter 
} from '../../data/parameterMetadata';

interface ImgixParams {
  [key: string]: any;
}

interface ParameterInfo {
  display_name?: string;
  category?: string;
  expects?: Array<any>;
  depends?: Array<string>;
  url?: string;
  short_description?: string;
}

interface ParamsEditorProps {
  title: string;
  titleKey: string; // Translation key
  defaultParams: any;
  originalCode: string;
  onParamsChange: (params: any, editorContent?: string) => void;
  currentImageSetId?: string | null;
  storageKeyPrefix: string; // "heroEditorContent_" or "productEditorContent_"
  className?: string;
}

function ParamsEditor({ 
  title, 
  titleKey,
  defaultParams, 
  originalCode, 
  onParamsChange, 
  currentImageSetId,
  storageKeyPrefix,
  className = ""
}: ParamsEditorProps) {
  // Store editor content for each image set
  const [editorContentMap, setEditorContentMap] = useState<{[key: string]: string}>(() => {
    // Initialize with default
    const initialMap: {[key: string]: string} = {
      default: originalCode
    };
    
    // If there's a current image set ID, try to load its stored editor content
    if (currentImageSetId) {
      try {
        // Use localStorage directly to avoid circular import with storageService
        const storageKey = `${storageKeyPrefix}${currentImageSetId}`;
        const storedContent = localStorage.getItem(storageKey);
        
        if (storedContent) {
          initialMap[currentImageSetId] = storedContent;
          console.log(`Loaded stored ${storageKeyPrefix} content for set:`, currentImageSetId);
        }
      } catch (e) {
        console.error('Error loading stored editor content:', e);
      }
    }
    
    return initialMap;
  });
  
  // Get the current editor content based on image set ID
  const currentEditorContent = currentImageSetId 
    ? editorContentMap[currentImageSetId] || originalCode
    : editorContentMap.default;
  const [error, setError] = useState<string | null>(null);
  const [selectedParam, setSelectedParam] = useState<string | null>(null);
  const [selectedParamValue, setSelectedParamValue] = useState<string | null>(null);
  const [parameterInfo, setParameterInfo] = useState<ParameterInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showParamPopover, setShowParamPopover] = useState<boolean>(false);
  const [paramSearchTerm, setParamSearchTerm] = useState<string>('');
  const [parameterList, setParameterList] = useState<{ name: string, displayName: string, category: string, description: string }[]>([]);
  const [isLoadingParams, setIsLoadingParams] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useLanguage();
  
  // Auto-adjust height of textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';
      
      // Calculate the actual height needed plus a little padding
      const newHeight = textareaRef.current.scrollHeight + 5;
      
      // Set the height
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [currentEditorContent]);
  
  // Listen for reset event to update editor content with original code
  useEffect(() => {
    const handleReset = () => {
      console.log(`${storageKeyPrefix}: Resetting to original code`);
      // Reset content for the current image set
      const newEditorContentMap = {...editorContentMap};
      const key = currentImageSetId || 'default';
      newEditorContentMap[key] = originalCode;
      setEditorContentMap(newEditorContentMap);
      
      onParamsChange(defaultParams, originalCode);
    };
    
    window.addEventListener('reset-parameters', handleReset);
    
    return () => {
      window.removeEventListener('reset-parameters', handleReset);
    };
  }, [originalCode, defaultParams, onParamsChange, editorContentMap, currentImageSetId, storageKeyPrefix]);
  
  // Update editor content when switching image sets
  useEffect(() => {
    if (currentImageSetId) {
      console.log(`${storageKeyPrefix}: Switching to image set`, currentImageSetId);
      
      // If we don't have editor content for this set yet, initialize it
      if (!editorContentMap[currentImageSetId]) {
        // Try to get content from localStorage as a fallback
        const storageKey = `${storageKeyPrefix}${currentImageSetId}`;
        const storedContent = localStorage.getItem(storageKey);
        
        setEditorContentMap(prev => ({
          ...prev,
          [currentImageSetId]: storedContent || originalCode
        }));
      }
    }
  }, [currentImageSetId, editorContentMap, originalCode, storageKeyPrefix]);

  // Function to get parameter info
  const fetchParameterInfo = async (param: string) => {
    setLoading(true);
    
    try {
      const response = await fetch('https://raw.githubusercontent.com/imgix/imgix-url-params/master/dist/parameters.json');
      if (!response.ok) {
        throw new Error('Failed to fetch parameter data');
      }
      
      const data = await response.json();
      
      // Search for parameter in the data (handle hyphenated parameters)
      let paramKey = param;
      if (!data.parameters[param] && param.includes('-')) {
        // Convert kebab-case to camelCase for lookup
        paramKey = param.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      }
      
      if (data.parameters[paramKey]) {
        setParameterInfo(data.parameters[paramKey]);
      } else {
        setParameterInfo(null);
      }
    } catch (error) {
      console.error('Error fetching parameter info:', error);
      setParameterInfo(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Function to load all parameters for the popover
  const loadAllParameters = async () => {
    setIsLoadingParams(true);
    
    try {
      const response = await fetch('https://raw.githubusercontent.com/imgix/imgix-url-params/master/dist/parameters.json');
      if (!response.ok) {
        throw new Error('Failed to fetch parameter data');
      }
      
      const data = await response.json();
      
      // Convert the parameters object to an array for easier filtering and sorting
      const paramArray = Object.entries(data.parameters).map(([key, value]: [string, any]) => ({
        name: key,
        displayName: value.display_name || key,
        category: value.category || 'Other',
        description: value.short_description || ''
      }));
      
      // Sort by category and then by display name
      paramArray.sort((a, b) => {
        if (a.category === b.category) {
          return a.displayName.localeCompare(b.displayName);
        }
        return a.category.localeCompare(b.category);
      });
      
      setParameterList(paramArray);
    } catch (error) {
      console.error('Error loading parameters:', error);
      setParameterList([]);
    } finally {
      setIsLoadingParams(false);
    }
  };
  
  // Open parameter browser popover
  const handleOpenParamBrowser = () => {
    setShowParamPopover(true);
    loadAllParameters();
    setParamSearchTerm('');
  };
  
  // Insert parameter at cursor position
  const insertParameter = (paramName: string) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;
    
    // Create the new parameter line with indentation matching the other parameters
    const newParam = `  ${paramName}: '',\n`;
    
    // Insert at cursor position
    const newContent = 
      currentEditorContent.substring(0, cursorPos) + 
      newParam + 
      currentEditorContent.substring(cursorPos);
    
    // Update the editor content for the current image set
    const key = currentImageSetId || 'default';
    setEditorContentMap(prev => ({
      ...prev,
      [key]: newContent
    }));
    setShowParamPopover(false);
    
    // Focus back on textarea and set cursor after the inserted parameter
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = cursorPos + newParam.length - 3; // Position cursor inside quotes
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // State to track if the selected parameter is commented out
  const [isSelectedParamCommented, setIsSelectedParamCommented] = useState<boolean>(false);

  // Handle parameter click in textarea
  const handleParamClick = () => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;
    
    // Find parameter at cursor position
    const text = currentEditorContent;
    const lines = text.split('\n');
    
    let currentPos = 0;
    for (const line of lines) {
      // Skip if cursor is not in this line
      if (cursorPos < currentPos || cursorPos > currentPos + line.length) {
        currentPos += line.length + 1; // +1 for newline character
        continue;
      }
      
      // Check if line is commented and handle it
      let processedLine = line;
      let isCommented = false;
      
      if (line.trim().startsWith('//')) {
        // Remove comment markers for processing, but remember it was commented
        processedLine = line.trim().substring(2).trim();
        isCommented = true;
      }
      
      // Find parameter in the processed line
      const colonPos = processedLine.indexOf(':');
      if (colonPos === -1) {
        currentPos += line.length + 1;
        continue;
      }
      
      // Capture parameter name regardless of cursor position on the line
      let paramName = processedLine.substring(0, colonPos).trim();
      
      // Remove quotes if present
      if ((paramName.startsWith('"') && paramName.endsWith('"')) || 
          (paramName.startsWith("'") && paramName.endsWith("'"))) {
        paramName = paramName.substring(1, paramName.length - 1);
      }
      
      // Skip empty parameter names
      if (!paramName) {
        currentPos += line.length + 1;
        continue;
      }
      
      // Try to extract parameter value (for detailed information about the specific value)
      let paramValue = null;
      const valueStr = processedLine.substring(colonPos + 1).trim();
      
      // Extract value from quotes or simple values
      const valueMatch = valueStr.match(/^['"](.*)['"],?$/);
      if (valueMatch && valueMatch[1] !== undefined) {
        paramValue = valueMatch[1];
      } else if (valueStr.endsWith(',')) {
        paramValue = valueStr.slice(0, -1);
      } else {
        paramValue = valueStr;
      }
      
      // The cursor is anywhere on this line (which has a valid parameter), 
      // so show the parameter details
      setSelectedParam(paramName);
      setSelectedParamValue(paramValue);
      setIsSelectedParamCommented(isCommented);
      fetchParameterInfo(paramName);
      return;
    }
    
    // No parameter found at cursor position
    setSelectedParam(null);
    setSelectedParamValue(null);
    setIsSelectedParamCommented(false);
    setParameterInfo(null);
  };

  // State to track editor content without auto-applying changes
  const [editorContent, setEditorContent] = useState<string>(currentEditorContent);

  // Update editorContent when currentEditorContent changes (e.g., when switching image sets)
  useEffect(() => {
    setEditorContent(currentEditorContent);
  }, [currentEditorContent]);

  // Handle text changes in the editor
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setEditorContent(newContent);
  };

  // Apply changes when apply button is clicked
  const handleApplyChanges = () => {
    // Update the editor content for the current image set
    const key = currentImageSetId || 'default';
    setEditorContentMap(prev => ({
      ...prev,
      [key]: editorContent
    }));

    try {
      // Extract active (uncommented) parameters from the code
      const activeParams: ImgixParams = {};

      // Split by lines and process each
      const lines = editorContent.split('\n');

      for (const line of lines) {
        let processedLine = line.trim();
        let isCommented = false;

        // Check if line is commented
        if (processedLine.startsWith('//')) {
          // Remove the comment markers to process the parameter
          processedLine = processedLine.substring(2).trim();
          isCommented = true;
        }

        // Skip empty lines or bracket-only lines
        if (processedLine === '' || processedLine === '{' || processedLine === '}') continue;

        // Extract key and value with careful handling of hyphenated keys
        const colonPos = processedLine.indexOf(':');
        if (colonPos === -1) continue;

        // Get key part
        let key = processedLine.substring(0, colonPos).trim();

        // Remove quotes from key if present
        if ((key.startsWith('"') && key.endsWith('"')) ||
            (key.startsWith("'") && key.endsWith("'"))) {
          key = key.substring(1, key.length - 1);
        }

        // Get value part
        const valueStr = processedLine.substring(colonPos + 1).trim();

        // Ignore empty values or just commas
        if (valueStr === '' || valueStr === ',') continue;

        // Only add parameters to activeParams if they are NOT commented out
        if (!isCommented) {
          // Handle quoted string values
          const valueMatch = valueStr.match(/^['"](.*)['"],?$/);
          if (valueMatch && valueMatch[1] !== undefined) {
            activeParams[key] = valueMatch[1];
          }
          // Handle boolean true/false
          else if (valueStr === 'true' || valueStr === 'true,') {
            activeParams[key] = true;
          }
          else if (valueStr === 'false' || valueStr === 'false,') {
            activeParams[key] = false;
          }
          // Handle numeric values
          else if (!isNaN(Number(valueStr.replace(',', '')))) {
            activeParams[key] = Number(valueStr.replace(',', ''));
          }
          // Handle special imgix parameters that need special handling
          else if (key === 'auto') {
            // Handle auto parameter (format,compress,etc.)
            const autoValues = valueStr.replace(/['"]/g, '').replace(',', '').split(',');
            activeParams[key] = autoValues.join(',');
          }
          // Handle bg-replace with themes (Christmas, etc.)
          else if (key === 'bg-replace') {
            const match = valueStr.match(/^['"](.*)['"],?$/);
            if (match && match[1] !== undefined) {
              activeParams[key] = match[1];
            } else {
              // If pattern doesn't match, use the raw string but clean it up
              activeParams[key] = valueStr.replace(/^['"]|['"],?$/g, '');
            }
          }
        }
      }

      console.log('Parameters applied. New parameters:', activeParams);
      setError(null);
      // Pass both the parsed parameters and the raw editor content
      onParamsChange(activeParams, editorContent);
    } catch (err) {
      setError('Error parsing parameters. Make sure your syntax is correct.');
    }
  };

  return (
    <div className={`bg-gray-800 p-4 rounded-lg ${className}`}>      
      <div className="flex items-center justify-between mb-2 border-b border-gray-700 pb-1">
        <h4 className="text-white font-medium">
          {t ? t(titleKey) : title}
        </h4>
        <button 
          onClick={handleOpenParamBrowser}
          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded flex items-center mr-1"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t ? t('add_parameter') : 'Add Parameter'}
        </button>
      </div>
      
      <div className="relative">
        <textarea
          ref={textareaRef}
          className="w-full min-h-[100px] bg-gray-900 text-gray-100 p-3 font-mono text-sm rounded resize-none overflow-hidden"
          value={editorContent}
          onChange={handleChange}
          onClick={handleParamClick}
          onKeyUp={handleParamClick}
          spellCheck={false}
          style={{ overflow: 'hidden' }}
        />

        <div className="flex justify-end mt-3">
          <button
            onClick={handleApplyChanges}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {t ? t('apply_changes') : 'Apply Changes'}
          </button>
        </div>

        {error && (
          <div className="mt-2 text-red-400 text-sm">{error}</div>
        )}
      </div>
      
      {/* Parameter Details Section */}
      {selectedParam && (
        <div className="mt-4 p-3 bg-gray-700 rounded border border-gray-600">
          <div className="flex flex-wrap items-center justify-between">
            <h5 className="font-medium text-gray-200 mb-2 flex items-center">
              <span className="text-blue-400 mx-1">{selectedParam}</span>
              
              {/* Current value (if any) */}
              {selectedParamValue && selectedParamValue !== "" && (
                <span className="mx-1 text-gray-400">=</span>
              )}
              {selectedParamValue && selectedParamValue !== "" && (
                <span className="text-green-400 mx-1">{selectedParamValue}</span>
              )}
            </h5>
          </div>
          
          {loading ? (
            <p className="text-gray-300 text-sm">Loading parameter details...</p>
          ) : parameterInfo ? (
            <div className="text-sm">
              {parameterInfo.short_description && (
                <p className="text-gray-200 mb-1">
                  {parameterInfo.short_description}
                </p>
              )}
              {parameterInfo.depends && parameterInfo.depends.length > 0 && (
                <p className="text-gray-200 mb-1">
                  <span className="font-medium text-gray-400">Depends on:</span> {parameterInfo.depends.join(', ')}
                </p>
              )}
              
              {/* Consolidated parameter and value information */}
              {getParameterMetadata(selectedParam) && (
                <div className="mt-3 p-2 bg-gray-800 rounded">
                  {/* Feature type with appropriate coloring */}
                  <p className="text-gray-200 mb-1">
                    <span className="font-medium text-gray-400">Feature Type:</span>{' '}
                    <span className={
                      // If we have a value and it's premium, or the parameter itself is premium
                      (selectedParamValue && getParameterValueMetadata(selectedParam, selectedParamValue)?.premium) || 
                      (!selectedParamValue && getParameterMetadata(selectedParam)?.premium) 
                        ? 'text-amber-300' 
                        : 'text-green-300'
                    }>
                      {/* Show value-specific feature type if available, otherwise parameter feature type */}
                      {selectedParamValue && getParameterValueMetadata(selectedParam, selectedParamValue)
                        ? getParameterValueMetadata(selectedParam, selectedParamValue)?.feature_type
                        : getParameterMetadata(selectedParam)?.feature_type}
                    </span>
                    
                    {/* Small badges for AI or Premium */}
                    {selectedParamValue && getParameterValueMetadata(selectedParam, selectedParamValue)?.ai_powered && (
                      <span className="ml-2 px-1 text-xxs rounded bg-purple-900 text-purple-200">AI</span>
                    )}
                    {selectedParamValue && getParameterValueMetadata(selectedParam, selectedParamValue)?.premium && (
                      <span className="ml-1 px-1 text-xxs rounded bg-amber-900 text-amber-200">Premium</span>
                    )}
                  </p>
                  
                  {/* Show value-specific notes if available and different from parameter notes */}
                  {selectedParamValue && 
                   getParameterValueMetadata(selectedParam, selectedParamValue)?.notes && 
                   getParameterValueMetadata(selectedParam, selectedParamValue)?.notes !== getParameterMetadata(selectedParam)?.notes && (
                    <p className="text-gray-300 text-xs mt-1">
                      {getParameterValueMetadata(selectedParam, selectedParamValue)?.notes}
                    </p>
                  )}
                  
                  {/* Show parameter notes if no value-specific notes or as fallback */}
                  {(!selectedParamValue || 
                    !getParameterValueMetadata(selectedParam, selectedParamValue)?.notes) && 
                    getParameterMetadata(selectedParam)?.notes && (
                    <p className="text-gray-300 text-xs mt-1">
                      {getParameterMetadata(selectedParam)?.notes}
                    </p>
                  )}
                  
                  {/* Show available values - from our metadata or from parameters.json */}
                  {(getParameterMetadata(selectedParam)?.values || 
                    (parameterInfo?.expects && 
                     parameterInfo.expects.some(expect => expect.type === 'list' && expect.possible_values))) && (
                    <div className="mt-2 border-t border-gray-700 pt-2">
                      <p className="text-gray-200 text-xs font-medium mb-1">Available Values:</p>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                        {/* First try to use our detailed metadata if available */}
                        {getParameterMetadata(selectedParam)?.values && 
                         Object.keys(getParameterMetadata(selectedParam)?.values || {}).length > 0 ? (
                          // Render values from our custom metadata
                          Object.entries(getParameterMetadata(selectedParam)?.values || {}).map(([value, metadata]) => (
                            <div key={value} className="text-xs flex items-center">
                              <span className={value === selectedParamValue ? 'text-green-300 font-semibold' : 'text-blue-300'}>
                                {value}
                              </span>
                              {metadata.premium && (
                                <span className="ml-1 px-1 text-xxs rounded bg-amber-900 text-amber-200">Pro</span>
                              )}
                              {metadata.ai_powered && (
                                <span className="ml-1 px-1 text-xxs rounded bg-purple-900 text-purple-200">AI</span>
                              )}
                            </div>
                          ))
                        ) : (
                          // Fall back to values from parameters.json if no custom metadata
                          parameterInfo?.expects?.flatMap(expect => 
                            expect.type === 'list' && expect.possible_values 
                              ? expect.possible_values.map((value: string) => (
                                  <div key={value} className="text-xs flex items-center">
                                    <span className={value === selectedParamValue ? 'text-green-300 font-semibold' : 'text-blue-300'}>
                                      {value}
                                    </span>
                                  </div>
                                ))
                              : []
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Documentation Link */}
              {(parameterInfo.url || getParameterMetadata(selectedParam)?.documentation_url) && (
                <div className="mt-3">
                  <a 
                    href={parameterInfo.url || getParameterMetadata(selectedParam)?.documentation_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline text-sm"
                  >
                    View Documentation
                  </a>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-300 text-sm">No details found for this parameter.</p>
          )}
        </div>
      )}
      
      {/* Parameter Browser Popover */}
      {showParamPopover && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-gray-800 w-full max-w-md mx-4 md:mx-0 rounded-lg shadow-lg overflow-hidden">
            <div className="flex justify-between items-center border-b border-gray-700 p-4">
              <h3 className="text-lg font-medium text-white">{t('parameter_browser_title')}</h3>
              <button
                onClick={() => setShowParamPopover(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              <input
                type="text"
                className="w-full p-2 mb-4 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder={t('parameter_browser_search')}
                value={paramSearchTerm}
                onChange={(e) => setParamSearchTerm(e.target.value)}
              />
              
              <div className="max-h-96 overflow-y-auto pr-2">
                {isLoadingParams ? (
                  <div className="text-center py-8 text-gray-400">
                    {t('parameter_browser_loading')}
                  </div>
                ) : (
                  <>
                    {parameterList.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        {t('parameter_browser_no_results')}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Group by category */}
                        {Array.from(new Set(parameterList.map(p => p.category))).map(category => {
                          const categoryParams = parameterList
                            .filter(p => p.category === category)
                            .filter(p => 
                              paramSearchTerm === '' || 
                              p.name.toLowerCase().includes(paramSearchTerm.toLowerCase()) ||
                              p.displayName.toLowerCase().includes(paramSearchTerm.toLowerCase())
                            );
                            
                          if (categoryParams.length === 0) return null;
                            
                          return (
                            <div key={category}>
                              <h4 className="text-sm font-medium text-gray-400 mb-2">{category}</h4>
                              <div className="pl-2 grid grid-cols-2 gap-2">
                                {categoryParams.map(param => (
                                  <button
                                    key={param.name}
                                    className="text-left text-sm text-blue-400 hover:text-blue-300 py-1 px-2 rounded hover:bg-gray-700 truncate flex items-center"
                                    title={`${param.displayName}${param.description ? ': ' + param.description : ''}`}
                                    onClick={() => insertParameter(param.name)}
                                  >
                                    <span>{param.displayName}</span>
                                    {isAiParameter(param.name) && (
                                      <span className="ml-1 px-1 text-xxs rounded bg-purple-900 text-purple-200">AI</span>
                                    )}
                                    {isPremiumParameter(param.name) && (
                                      <span className="ml-1 px-1 text-xxs rounded bg-amber-900 text-amber-200">PRO</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <div className="border-t border-gray-700 p-4 flex justify-end">
              <button
                onClick={() => setShowParamPopover(false)}
                className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
              >
                {t('parameter_browser_cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParamsEditor;